import type { LucideIcon } from 'lucide-react';
import {
    Cable,
    Headset,
    LineChart,
    MessagesSquare,
    Puzzle,
    ServerCog,
    ShieldCheck,
    Workflow,
} from 'lucide-react';
import React from 'react';
import { useReveal } from '../utils/useReveal';
import SectionLayout from './SectionLayout';

const Features: React.FC = () => {
  const { ref, isVisible } = useReveal();
  const features = [
    {
      icon: MessagesSquare as LucideIcon,
      title: 'Omnichannel Support',
      desc: 'Kelola semua channel komunikasi (WhatsApp, Email, Chat, Social Media) dalam satu platform terpadu.',
    },
    {
      icon: Puzzle as LucideIcon,
      title: 'Custom Solutions',
      desc: 'Solusi yang dapat dikustomisasi sesuai kebutuhan spesifik bisnis Anda dengan fleksibilitas tinggi.',
    },
    {
      icon: LineChart as LucideIcon,
      title: 'Real-time Analytics',
      desc: 'Dashboard analitik real-time untuk memantau performa dan membuat keputusan data-driven.',
    },
    {
      icon: Workflow as LucideIcon,
      title: 'Workflow Automation',
      desc: 'Otomasi proses bisnis untuk meningkatkan efisiensi dan mengurangi beban kerja manual.',
    },
    {
      icon: Cable as LucideIcon,
      title: 'API Integration',
      desc: 'Integrasi mudah dengan sistem existing Anda melalui API yang powerful dan dokumentasi lengkap.',
    },
    {
      icon: ShieldCheck as LucideIcon,
      title: 'Enterprise Security',
      desc: 'Keamanan tingkat enterprise dengan enkripsi data, backup otomatis, dan compliance standar industri.',
    },
    {
      icon: ServerCog as LucideIcon,
      title: 'Scalable Infrastructure',
      desc: 'Infrastruktur yang dapat diskalakan sesuai pertumbuhan bisnis Anda tanpa batasan.',
    },
    {
      icon: Headset as LucideIcon,
      title: '24/7 Support',
      desc: 'Tim support siap membantu Anda kapan saja dengan response time yang cepat dan profesional.',
    },
  ];

  return (
    <>
      <style>{`
        .services-section {
          opacity: 0;
          transform: translateY(50px);
          transition: opacity 0.8s ease-out, transform 0.8s ease-out;
        }

        .services-section.revealed {
          opacity: 1;
          transform: translateY(0);
        }

        .facility-grid {
          display: grid;
          grid-template-columns: repeat(12, 1fr);
          grid-auto-rows: minmax(200px, auto);
          gap: 16px;
          max-width: 1400px;
          margin: 0 auto;
        }

        .facility-card {
          background: #ffffff;
          border: 1px solid rgba(10, 22, 40, 0.08);
          border-radius: 20px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          padding: 32px;
          box-shadow: 0 4px 20px rgba(15, 30, 53, 0.06);
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          cursor: pointer;
        }

        .facility-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: #0a1628;
          opacity: 0;
          transition: opacity 0.4s ease;
          border-radius: 20px;
        }

        .facility-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 40px rgba(15, 30, 53, 0.12);
          border-color: #0a1628;
        }

        .facility-card:hover::before {
          opacity: 1;
        }

        .facility-card:hover .content {
          color: #ffffff;
        }

        .facility-card:hover h4 {
          color: #ffffff;
        }

        .facility-card:hover p {
          color: rgba(255, 255, 255, 0.9);
        }

        .facility-card:hover .facility-icon {
          background: rgba(255, 255, 255, 0.2);
          color: #ffffff;
        }

        .facility-card .content {
          position: relative;
          z-index: 1;
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .facility-header {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          margin-bottom: 16px;
        }

        .facility-icon {
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 14px;
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(147, 51, 234, 0.15) 100%);
          color: #0a1628;
          flex-shrink: 0;
          transition: transform 0.4s ease;
        }

        .facility-card:hover .facility-icon {
          transform: scale(1.1) rotate(5deg);
        }

        .facility-icon svg {
          width: 24px;
          height: 24px;
        }

        .facility-card h4 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
          color: #0a1628;
          line-height: 1.3;
        }

        .facility-card p {
          font-size: 15px;
          color: rgba(10, 22, 40, 0.7);
          line-height: 1.6;
          flex: 1;
          margin: 0;
        }

        /* Magic Bento Grid Layout - Ukuran berbeda untuk setiap card */
        .facility-card:nth-child(1) {
          grid-column: span 6;
          grid-row: span 1;
        }

        .facility-card:nth-child(2) {
          grid-column: span 6;
          grid-row: span 1;
        }

        .facility-card:nth-child(3) {
          grid-column: span 4;
          grid-row: span 1;
        }

        .facility-card:nth-child(4) {
          grid-column: span 4;
          grid-row: span 1;
        }

        .facility-card:nth-child(5) {
          grid-column: span 4;
          grid-row: span 1;
        }

        .facility-card:nth-child(6) {
          grid-column: span 3;
          grid-row: span 1;
        }

        .facility-card:nth-child(7) {
          grid-column: span 3;
          grid-row: span 1;
        }

        .facility-card:nth-child(8) {
          grid-column: span 6;
          grid-row: span 1;
        }

        @media (max-width: 1200px) {
          .facility-grid {
            grid-template-columns: repeat(8, 1fr);
          }

          .facility-card:nth-child(1) {
            grid-column: span 4;
            grid-row: span 1;
          }

          .facility-card:nth-child(2) {
            grid-column: span 4;
            grid-row: span 1;
          }

          .facility-card:nth-child(3) {
            grid-column: span 4;
            grid-row: span 1;
          }

          .facility-card:nth-child(4) {
            grid-column: span 4;
            grid-row: span 1;
          }

          .facility-card:nth-child(5) {
            grid-column: span 4;
            grid-row: span 1;
          }

          .facility-card:nth-child(6) {
            grid-column: span 4;
            grid-row: span 1;
          }

          .facility-card:nth-child(7) {
            grid-column: span 4;
            grid-row: span 1;
          }

          .facility-card:nth-child(8) {
            grid-column: span 8;
            grid-row: span 1;
          }
        }

        @media (max-width: 768px) {
          .facility-grid {
            grid-template-columns: repeat(4, 1fr);
            gap: 12px;
          }

          .facility-card {
            grid-column: span 4 !important;
            grid-row: span 1 !important;
            padding: 24px;
          }
        }

        @media (max-width: 480px) {
          .facility-grid {
            grid-template-columns: 1fr;
          }

          .facility-card {
            grid-column: span 1 !important;
          }
        }
      `}</style>
      <SectionLayout id="fitur" maxWidth="2xl">
        <section 
          className={`services-section ${isVisible ? 'revealed' : ''}`} 
          ref={ref as React.RefObject<HTMLElement>}
        >
        <div className="section-header">
          <h2 className="section-title">Fitur Unggulan</h2>
          <p className="section-description">
            Fitur-fitur canggih yang dirancang untuk meningkatkan efisiensi dan produktivitas bisnis Anda.
          </p>
        </div>
        <div className="facility-grid">
          {features.map((feature, idx) => {
            const Icon = feature.icon;
            return (
            <div key={idx} className="facility-card">
              <div className="content">
                  <div className="facility-header">
                    <div className="facility-icon">
                      <Icon />
                    </div>
                <h4>{feature.title}</h4>
                  </div>
                <p>{feature.desc}</p>
              </div>
            </div>
            );
          })}
        </div>
        </section>
      </SectionLayout>
    </>
  );
};

export default Features;

